/**
 * Cinema Store — the global "hide all UI" switch.
 *
 * Cinema mode blanks every piece of chrome over the 3D viewport (islands,
 * panels, toolbars, the map-style selector) plus the nav sidebar and icon rail,
 * so the viewport can be screen-captured cleanly. There is no in-app recorder by
 * design — this exists to make an external capture (OBS / Game Bar) look right.
 *
 * Sidebar hiding delegates to `sidebarStore`'s existing focus mode rather than
 * introducing a second sidebar-hiding path: `enterFocusMode()` snapshots the
 * prior `mainState`/`railVisible` into a module-level ref and `exitFocusMode()`
 * restores it, so a user who was running collapsed gets collapsed back.
 *
 * `source` distinguishes a manual toggle from one the flythrough driver engaged
 * for a playlist with `hideUi` — so stopping playback only un-hides the UI if
 * playback is what hid it.
 *
 * Deliberately NOT persisted: a reload must never strand the user in a
 * chromeless app with no obvious way out.
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useSidebarStore } from './sidebarStore'

export type CinemaSource = 'manual' | 'playback'

interface CinemaState {
  /** True while all UI is hidden. */
  active: boolean
  /** What turned it on — so playback only reverses its own change. */
  source: CinemaSource | null

  enter: (source: CinemaSource) => void
  exit: () => void
  toggle: () => void
}

export const useCinemaStore = create<CinemaState>()(
  devtools(
    (set, get) => ({
      active: false,
      source: null,

      enter: (source) => {
        if (get().active) return
        useSidebarStore.getState().enterFocusMode()
        set({ active: true, source }, false, 'cinema/enter')
      },

      exit: () => {
        if (!get().active) return
        useSidebarStore.getState().exitFocusMode()
        set({ active: false, source: null }, false, 'cinema/exit')
      },

      toggle: () => {
        const { active, enter, exit } = get()
        if (active) exit()
        else enter('manual')
      },
    }),
    { name: 'CinemaStore' }
  )
)
